
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Palette, ListOrdered, Undo2, Columns2, ZoomIn, Image } from 'lucide-react';
import { TemplatePreview } from './TemplatePreview';
import { ColorSettings } from './ColorSettings';
import { SectionSettings } from './SectionSettings';
import { Button } from '@/components/ui/button';
import { TemplateStyle } from './templateData';
import { templateGenerators } from '@/utils/pdf-templates';
import { mockPackageData } from '@/utils/pdf-templates/mockData';
import { Column } from '@/utils/pdf-templates/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { MediaSettings } from './MediaSettings';

interface TemplateCardProps {
    template: { id: string; name: string; };
    settings: TemplateStyle;
    onUpdate: (newSettings: Partial<TemplateStyle>) => void;
    onReset: () => void;
}

export function TemplateCard({ template, settings, onUpdate, onReset }: TemplateCardProps) {
    const [htmlContent, setHtmlContent] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const generatePreview = async () => {
            if (!settings) return;
            setIsLoading(true);
            try {
                const enhancedMockData = {
                    ...mockPackageData,
                    clientType: 'b2b' as const,
                    companyName: 'شركة وهمية للمعاينة',
                    companyLogoUrl: '/placeholder.svg',
                };
                const generator = templateGenerators[template.id as keyof typeof templateGenerators] || templateGenerators.template1;
                const html = await generator(enhancedMockData, settings);
                setHtmlContent(html);
            } catch (error) {
                console.error(`Failed to generate preview for ${template.id}:`, error);
                setHtmlContent('<div style="padding: 20px; text-align: center; color: red;">فشل في تحميل المعاينة</div>');
            } finally {
                setIsLoading(false);
            }
        };
        generatePreview();
    }, [settings, template.id]);
    
    if (!settings) return null;

    const handleColorChange = (colorName: string, value: string) => {
        onUpdate({
            colors: { ...settings.colors, [colorName]: value }
        });
    };

    const handleColumnsChange = (newColumns: Column[]) => {
        onUpdate({ columns: newColumns });
    };

    const handleLayoutGridColsChange = (cols: number) => {
        onUpdate({ layoutGridCols: cols });
    };

    const handleMediaChange = (mediaType: 'logo' | 'background', value: string | undefined) => {
        onUpdate({
            media: {
                ...settings.media,
                [mediaType]: value
            }
        });
    };

    return (
        <Card className="overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="p-4 border-b bg-gray-50/50">
                <TemplatePreview htmlContent={htmlContent} isLoading={isLoading} />
                <div className="flex justify-between items-center mt-4">
                    <div>
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <CardDescription>انقر على التبويبات للتخصيص</CardDescription>
                    </div>
                    <div className="flex items-center gap-1">
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="معاينة أكبر" className="text-gray-500 hover:text-gray-800">
                                    <ZoomIn className="h-4 w-4" />
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
                                <DialogHeader>
                                    <DialogTitle>معاينة حية: {template.name}</DialogTitle>
                                </DialogHeader>
                                <div className="flex-1 overflow-auto">
                                    {isLoading ? (
                                        <div className="w-full h-full flex items-center justify-center text-gray-500">جاري التحميل...</div>
                                    ) : (
                                        <iframe
                                            srcDoc={htmlContent}
                                            title={`Large preview for ${template.name}`}
                                            className="w-full h-full border-0 rounded-md"
                                            sandbox="allow-same-origin"
                                        />
                                    )}
                                </div>
                            </DialogContent>
                        </Dialog>
                        <Button variant="ghost" size="icon" onClick={onReset} aria-label="إعادة تعيين" className="text-gray-500 hover:text-gray-800">
                            <Undo2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <Tabs defaultValue="sections" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 rounded-none h-12">
                        <TabsTrigger value="colors" className="text-sm"><Palette className="w-4 h-4 ml-2"/> الألوان</TabsTrigger>
                        <TabsTrigger value="sections" className="text-sm"><ListOrdered className="w-4 h-4 ml-2"/> التخطيط</TabsTrigger>
                        <TabsTrigger value="media" className="text-sm"><Image className="w-4 h-4 ml-2"/> الوسائط</TabsTrigger>
                    </TabsList>
                    <TabsContent value="colors" className="p-4">
                        <ColorSettings
                            colors={settings.colors}
                            onColorChange={handleColorChange}
                        />
                    </TabsContent>
                    <TabsContent value="sections" className="p-4">
                        <div className="mb-4 pb-4 border-b">
                            <label className="text-sm font-medium flex items-center gap-2 text-gray-800">
                                <Columns2 className="w-4 h-4" />
                                عدد الأعمدة في لوحة الترتيب
                            </label>
                            <p className="text-xs text-gray-500 mt-1 mb-3">اختر عدد الأعمدة لعرضها في منطقة الترتيب أدناه.</p>
                            <div className="flex items-center gap-2">
                                {[1, 2, 3].map(num => (
                                    <Button 
                                        key={num}
                                        variant={settings.layoutGridCols === num ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handleLayoutGridColsChange(num)}
                                        className="w-12"
                                    >
                                        {num}
                                    </Button>
                                ))}
                            </div>
                        </div>
                        <SectionSettings
                            columns={settings.columns}
                            onColumnsChange={handleColumnsChange}
                            gridCols={settings.layoutGridCols || 1}
                        />
                    </TabsContent>
                    <TabsContent value="media" className="p-4">
                        <MediaSettings
                            media={settings.media || {}}
                            onMediaChange={handleMediaChange}
                        />
                    </TabsContent>
                </Tabs>
            </CardContent>
        </Card>
    );
}
